import { FunctionComponent, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { LoadingBlock } from "@/components/Spinner/LoadingBlock";
import { Button } from "@/components/Button/Button";
import { ButtonColor, ButtonSize } from "@/components/Button/types";
import { InputField } from "@/components/InputField/InputField";
import {
	AutoCompleteItem as DropdownAutoCompleteItem,
	Dropdown,
} from "@/components/Dropdown/Dropdown";
import { useSnackBar } from "@/utils/useSnackbar";

import {
	useGetSalesforceOwnersQuery,
	useGetSalesforceSourceFieldsQuery,
} from "@/pages/Private/redux/salesforce/salesforce.api";
import {
	useGetSalesforceDealConfigQuery,
	useGetSalesforcePipelinesQuery,
	useSaveSalesforceDealConfigMutation,
} from "@/pages/Private/redux/salesforce/salesforceDeal.api";

import { UpdateSalesforceDealConfig } from "../schema/salesforceDeal";
import { SalesforcePipelinePicker } from "./SalesforcePipelinePicker";
import { resolveSelectableStageId, resolveWritablePipelineId } from "./salesforcePipeline.utils";

interface DealConfigDraft {
	defaultPipelineId: string | null;
	defaultStageId: string | null;
	dealNameTemplate: string;
	closeDateOffsetDays: number;
	defaultOwnerId: string | null;
	defaultAmount: number | null;
}

const EMPTY_DRAFT: DealConfigDraft = {
	defaultPipelineId: null,
	defaultStageId: null,
	dealNameTemplate: "",
	closeDateOffsetDays: 30,
	defaultOwnerId: null,
	defaultAmount: null,
};

/**
 * `PUT deal-config` patches with `@IsOptional()` fields, so an unset value has to be
 * **omitted** — sending `null` fails validation.
 */
const buildPayload = (draft: DealConfigDraft): UpdateSalesforceDealConfig => {
	const payload: UpdateSalesforceDealConfig = {
		closeDateOffsetDays: draft.closeDateOffsetDays,
	};

	if (draft.defaultPipelineId) {
		payload.defaultPipelineId = draft.defaultPipelineId;
	}

	if (draft.defaultStageId) {
		payload.defaultStageId = draft.defaultStageId;
	}

	if (draft.dealNameTemplate.trim()) {
		payload.dealNameTemplate = draft.dealNameTemplate.trim();
	}

	if (draft.defaultOwnerId) {
		payload.defaultOwnerId = draft.defaultOwnerId;
	}

	if (draft.defaultAmount !== null) {
		payload.defaultAmount = draft.defaultAmount;
	}

	return payload;
};

/**
 * Opportunity defaults.
 *
 * The fields are named after the columns — `closeDateOffsetDays` and `defaultAmount` — not
 * after the Dynamics equivalents the plan's §4.4/§4.6 still use (`expectedCloseDateOffset`,
 * `estimatedValue`). Reading the Dynamics names here would render permanently empty inputs
 * and silently discard whatever the customer typed.
 */
export const SalesforceDealConfigTab: FunctionComponent = () => {
	const { t } = useTranslation();
	const ts = (key: string) => t(`company.salesforce.dealConfig.${key}`);

	const { handleOpenSnackbar, renderMessageSnackbar } = useSnackBar();

	const { data: dealConfig, isLoading: isLoadingConfig } = useGetSalesforceDealConfigQuery();
	const { data: pipelinesData } = useGetSalesforcePipelinesQuery();
	const { data: ownersData } = useGetSalesforceOwnersQuery();
	const { data: sourceFields } = useGetSalesforceSourceFieldsQuery();
	const [saveDealConfig, { isLoading: isSaving }] = useSaveSalesforceDealConfigMutation();

	const [draft, setDraft] = useState<DealConfigDraft>(EMPTY_DRAFT);

	const pipelines = useMemo(() => pipelinesData?.pipelines ?? [], [pipelinesData]);

	useEffect(() => {
		if (!dealConfig) {
			return;
		}

		setDraft({
			defaultPipelineId: dealConfig.defaultPipelineId ?? null,
			defaultStageId: dealConfig.defaultStageId ?? null,
			dealNameTemplate: dealConfig.dealNameTemplate ?? "",
			closeDateOffsetDays: dealConfig.closeDateOffsetDays ?? 30,
			defaultOwnerId: dealConfig.defaultOwnerId ?? null,
			defaultAmount: dealConfig.defaultAmount ?? null,
		});
	}, [dealConfig]);

	const ownerOptions: DropdownAutoCompleteItem[] = useMemo(
		() =>
			(ownersData?.owners ?? []).map((owner) => ({
				id: owner.id,
				title: owner.name ? `${owner.name} (${owner.email})` : owner.email,
			})),
		[ownersData]
	);

	const placeholderOptions: DropdownAutoCompleteItem[] = useMemo(
		() =>
			(sourceFields?.opportunityFields ?? sourceFields?.contactFields ?? []).map((field) => ({
				id: field.name,
				title: `{${field.name}} - ${field.label}`,
			})),
		[sourceFields]
	);

	const handleSave = async () => {
		try {
			// A synthesised pipeline's id is Signalist's own invention and is never written to
			// `RecordTypeId`; a stage from another record type would be rejected by the org.
			const pipelineId = resolveWritablePipelineId(pipelines, draft.defaultPipelineId);
			const stageId = resolveSelectableStageId(
				pipelines,
				draft.defaultPipelineId,
				draft.defaultStageId
			);

			await saveDealConfig(
				buildPayload({ ...draft, defaultPipelineId: pipelineId, defaultStageId: stageId })
			).unwrap();
			handleOpenSnackbar(ts("saved"));
		} catch {
			handleOpenSnackbar(ts("saveError"));
		}
	};

	if (isLoadingConfig) {
		return <LoadingBlock />;
	}

	return (
		<div className="flex w-full flex-col gap-4">
			{renderMessageSnackbar()}
			<div className="mb-1 text-sm text-text-meta">{ts("description")}</div>

			<div className="grid grid-cols-1 gap-4 md:grid-cols-2">
				<SalesforcePipelinePicker
					pipelineId={draft.defaultPipelineId}
					pipelines={pipelines}
					stageId={draft.defaultStageId}
					ts={ts}
					onChange={({ pipelineId, stageId }) =>
						setDraft((prev) => ({
							...prev,
							defaultPipelineId: pipelineId,
							defaultStageId: stageId,
						}))
					}
				/>

				<div className="flex flex-col gap-2 md:col-span-2">
					<InputField
						handleChange={(event) =>
							setDraft((prev) => ({ ...prev, dealNameTemplate: event.target.value }))
						}
						information={ts("dealNameTemplateHelp")}
						label={ts("dealNameTemplate")}
						name="dealNameTemplate"
						placeholder="{firstName} {lastName} - {company}"
						value={draft.dealNameTemplate}
					/>
					{placeholderOptions.length > 0 && (
						<div className="max-w-[400px]">
							<Dropdown
								data={placeholderOptions}
								floating={true}
								handleSelect={(value) =>
									setDraft((prev) => ({
										...prev,
										dealNameTemplate: `${prev.dealNameTemplate}{${value.id}}`,
									}))
								}
								label={ts("insertPlaceholder")}
							/>
						</div>
					)}
				</div>

				<InputField
					handleChange={(event) =>
						setDraft((prev) => ({
							...prev,
							closeDateOffsetDays: parseInt(event.target.value, 10) || 0,
						}))
					}
					information={ts("closeDateOffsetHelp")}
					label={ts("closeDateOffsetDays")}
					max={3650}
					min={0}
					name="closeDateOffsetDays"
					testId="salesforce-close-date-offset"
					type="number"
					value={draft.closeDateOffsetDays}
				/>

				<InputField
					handleChange={(event) => {
						const parsed = parseFloat(event.target.value);

						setDraft((prev) => ({
							...prev,
							defaultAmount: Number.isFinite(parsed) && parsed >= 0 ? parsed : null,
						}));
					}}
					information={ts("defaultAmountHelp")}
					label={ts("defaultAmount")}
					min={0}
					name="defaultAmount"
					testId="salesforce-default-amount"
					type="number"
					value={draft.defaultAmount ?? ""}
				/>

				<Dropdown
					data={ownerOptions}
					defaultValue={ownerOptions.find((option) => option.id === draft.defaultOwnerId)}
					floating={true}
					handleSelect={(value) =>
						setDraft((prev) => ({ ...prev, defaultOwnerId: value.id as string }))
					}
					label={ts("defaultOwner")}
				/>
			</div>

			<div className="mt-4 flex justify-end">
				<Button
					color={ButtonColor.ACTION}
					isLoading={isSaving}
					size={ButtonSize.S}
					testId="salesforce-deal-config-save"
					title={t("basics.save")}
					type="button"
					onClick={handleSave}
				/>
			</div>
		</div>
	);
};
